/**
 * Analytics Manager
 * Orchestrates multiple analytics providers with error handling and parallel execution
 */

import type { AnalyticsProvider, AnalyticsConfig } from "@artha/types";

// Create the error class locally
class AnalyticsProviderError extends Error {
  constructor(
    public readonly providerName: string,
    message: string,
    public readonly originalError?: Error,
  ) {
    super(`[${providerName}] ${message}`);
    this.name = "AnalyticsProviderError";
  }
}
import { AnalyticsRegistry } from "./providers/registry";
import { createPostHogProvider } from "./providers/posthog";
import { createCustomerIOProvider } from "./providers/customer-io-provider";

export class AnalyticsManager {
  private registry = new AnalyticsRegistry();
  private config: AnalyticsConfig;
  private initialized = false;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.initializeProviders();
  }

  private initializeProviders(): void {
    // Register PostHog provider
    if (this.config.posthog.enabled) {
      const posthogProvider = createPostHogProvider(true);
      this.registry.register(posthogProvider);
    }

    // Register CustomerProvider
    if (this.config.customerio.enabled) {
      const customerProvider = createCustomerIOProvider(
        true,
        this.config.customerio.siteId,
      );
      this.registry.register(customerProvider);
    }

    // Register Amplitude provider (if configured)
    // TODO: Implement AmplitudeProvider when needed

    // Register Mixpanel provider (if configured)
    // TODO: Implement MixpanelProvider when needed
  }

  /**
   * Initialize all registered providers
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      await this.registry.initialize();
      this.initialized = true;
    } catch (error) {
      console.error("Failed to initialize Analytics Manager:", error);
      throw error;
    }
  }

  /**
   * Track an event across all providers
   */
  async track(
    eventName: string,
    properties?: Record<string, unknown>,
  ): Promise<void> {
    const providers = this.registry.getProviders();

    if (providers.length === 0) {
      console.warn("No analytics providers available for tracking");
      return;
    }

    // Execute tracking in parallel across all providers
    const promises = providers.map((provider) =>
      this.safeExecute(
        () => provider.track(eventName, properties),
        provider.name,
        "track",
        { eventName, properties },
      ),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Identify a user across all providers
   */
  async identify(
    userId: string,
    properties?: Record<string, unknown>,
  ): Promise<void> {
    const providers = this.registry.getProviders();

    if (!userId) {
      return;
    }

    const promises = providers.map((provider) =>
      this.safeExecute(
        () => provider.identify(userId, properties),
        provider.name,
        "identify",
        { userId, properties },
      ),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Track a page view across all providers
   */
  async page(
    pageName?: string,
    properties?: Record<string, unknown>,
  ): Promise<void> {
    const providers = this.registry.getProviders();

    const promises = providers.map((provider) =>
      this.safeExecute(
        () => provider.page(pageName, properties),
        provider.name,
        "page",
        { pageName, properties },
      ),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Set group information across all providers
   */
  async group(
    groupType: string,
    groupId: string,
    properties?: Record<string, unknown>,
  ): Promise<void> {
    const providers = this.registry.getProviders();

    const promises = providers.map((provider) =>
      this.safeExecute(
        () => provider.group(groupType, groupId, properties),
        provider.name,
        "group",
        { groupType, groupId, properties },
      ),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Alias a user across all providers
   */
  async alias(newId: string, previousId?: string): Promise<void> {
    const providers = this.registry.getProviders();

    const promises = providers.map((provider) =>
      this.safeExecute(
        () => provider.alias(newId, previousId),
        provider.name,
        "alias",
        { newId, previousId },
      ),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Reset all providers
   */
  async reset(): Promise<void> {
    const providers = this.registry.getProviders();

    const promises = providers.map((provider) =>
      this.safeExecute(() => provider.reset(), provider.name, "reset"),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Get a specific provider (for provider-specific features)
   */
  getProvider<T extends AnalyticsProvider>(name: string): T | undefined {
    return this.registry.getProvider(name) as T | undefined;
  }

  /**
   * Get all enabled providers
   */
  getProviders(): AnalyticsProvider[] {
    return this.registry.getProviders();
  }

  /**
   * Get provider statistics
   */
  getStats(): {
    totalProviders: number;
    enabledProviders: number;
    providerNames: string[];
    initialized: boolean;
  } {
    return {
      totalProviders: this.registry.getProviderNames().length,
      enabledProviders: this.registry.getEnabledProviderCount(),
      providerNames: this.registry.getProviderNames(),
      initialized: this.initialized,
    };
  }

  /**
   * Safely execute a provider method with error handling
   */
  private async safeExecute(
    fn: () => Promise<void>,
    providerName: string,
    method: string,
    context?: Record<string, unknown>,
  ): Promise<void> {
    try {
      await fn();
    } catch (error) {
      const errorMessage =
        error instanceof AnalyticsProviderError
          ? error.message
          : `Failed to execute ${method}`;

      console.error("[Analytics] Provider execution failed", {
        errorMessage,
        provider: providerName,
        method,
        context,
        error,
      });

      // Optional: Send error to error tracking service
      // this.trackError(providerName, method, error, context);
    }
  }

  /**
   * Update configuration and reinitialize providers
   */
  async updateConfig(newConfig: Partial<AnalyticsConfig>): Promise<void> {
    this.config = { ...this.config, ...newConfig };

    // Destroy existing providers
    await this.registry.destroy();

    // Reinitialize with new config
    this.initialized = false;
    this.initializeProviders();
    await this.initialize();
  }

  /**
   * Destroy the analytics manager
   */
  async destroy(): Promise<void> {
    await this.registry.destroy();
    this.initialized = false;
  }
}
